import { paramsSerializer } from "@core/helpers"
import axios from "@core/plugins/axios.plugin"
import { handleServiceCall } from "@services/helpers"
import {
    Building,
    WorkflowEnedis,
    BuildingSourcePayload,
    Address,
} from "@types"
import { useEventBus } from "@vueuse/core"

// =====================
// Type definitions des réponses des services
// =====================
export type DataBuildingByIdResponse = {
    /**
     * Données de la source
     */
    building: {
        data: {
            date: string
        }[]
    }
    source: {
        data: {
            date: string
        }[]
        _id_source: string
    }
    /**
     * Liste de données de variables sur une période
     */
    periode: {
        data: object
    }
}

export type BuildingByIdResponse = Building

export type WorkflowEnedisResponseBuilding = WorkflowEnedis[]

export type GetBuildingByCompanyIdResponse = { data: Building[]; total: number }

/** Réponse paginée liste bâtiments (dashboard / recherche). */
export type GetBuildingsResponse = {
    total: number
    data: Building[]
    has_more?: boolean
}

export type GetBuildingsParams = {
    sort?: string
    order?: string
    tags?: string[] | null
    search?: string | null
    tree?: boolean
    level?: string[] | null
    page?: number
    limit?: number
    _id_user?: string | null
    signal?: AbortSignal
}

/**
 * Géométrie GeoJSON MultiPolygon (parcelle cadastrale), alignée api-metier validation / address.
 */
export type ParcelLocationMultiPolygon = {
    type: "MultiPolygon"
    coordinates: number[][][][]
}

/**
 * Options pour la création d'un bâtiment (aligné api-metier building.add.route)
 */
export type CreateBuildingOptions = {
    level?: string
    occupation?: string
    id_building?: string
    /** Parcelle manuelle (MultiPolygon) si l’open data ne fournit pas les coordonnées — aligné api-metier POST /building */
    parcel_location?: ParcelLocationMultiPolygon
    _id_folder?: string
}

/**
 * Payload pour la mise à jour de l'adresse d'un bâtiment (aligné api-metier building.put.address)
 */
export type UpdateBuildingAddressPayload = {
    address: Address
    level?: string
    id_building?: string
    /** Parcelle manuelle (MultiPolygon) si l’open data ne fournit pas les coordonnées — aligné api-metier address */
    parcel_location?: ParcelLocationMultiPolygon
}

/**
 * Une entrée du corps de POST /api/metier/building/batch (création lot).
 */
export type CreateBuildingBatchItem = {
    name: string
    address: Address | null
    location: {
        coordinates: [number, number] | number[] | null
    }
}

/**
 * Le service permettant de gérer les bâtiments de l'application.
 */
export class BuildingService {
    /**
     * Récupère une page de bâtiments d'une organisation (appel API paginé).
     * @param _id_company - Identifiant de l'organisation
     * @param page - Numéro de page (1-based)
     * @param limit - Nombre d'éléments par page
     */
    /**
     * GET /api/metier/building — liste utilisateur (tri, tags, recherche, arbre), aligné legacy getBuildings.
     */
    async getBuildings(
        params: GetBuildingsParams = {}
    ): Promise<GetBuildingsResponse> {
        return handleServiceCall(async () => {
            const {
                sort = "createdAt",
                order = "asc",
                tags,
                search,
                tree = true,
                level,
                page = 1,
                limit = 10,
                _id_user,
                signal,
            } = params
            const {
                data: { data, total, has_more },
            } = await axios.get(`/api/metier/building`, {
                signal,
                paramsSerializer,
                params: {
                    sort,
                    order,
                    tags,
                    search,
                    tree,
                    level,
                    limit,
                    page,
                    _id_user,
                },
            })
            return {
                total: total ?? 0,
                data: data ?? [],
                has_more,
            }
        })
    }

    /**
     * PUT /api/metier/building/:_id_building — mise à jour partielle.
     * Tu peux envoyer des chemins MongoDB en notation pointée pour ne modifier
     * qu'une sous-propriété sans reconstruire tout `infos`, par ex. :
     * `{ "infos.logement.nombre_occupants": 4 }`.
     */
    async updateBuildingById(
        buildingId: string,
        payload: Record<string, unknown>,
        controller?: AbortController
    ): Promise<Building> {
        return handleServiceCall<Building>(async () => {
            const { data } = await axios.put(
                `/api/metier/building/${buildingId}`,
                payload,
                { signal: controller?.signal }
            )
            return data
        })
    }

    async getBuildingsByCompanyIdPage(
        _id_company: string,
        page: number,
        limit: number
    ): Promise<GetBuildingByCompanyIdResponse> {
        return handleServiceCall<GetBuildingByCompanyIdResponse>(async () => {
            const { data } = await axios.get(`/api/metier/building`, {
                params: {
                    _id_company,
                    page,
                    limit,
                },
            })
            return {
                data: data.data ?? [],
                total: data.total ?? 0,
            }
        })
    }

    /**
     * Récupère les bâtiments d'une organisation (toutes les pages, avec event bus)
     * @param _id_company - Identifiant de l'organisation
     */
    async getBuildingsByCompanyId(
        _id_company: string,
        { eventBusName = "update:buildings" } = {
            eventBusName: "update:buildings",
        }
    ): Promise<GetBuildingByCompanyIdResponse> {
        return handleServiceCall<GetBuildingByCompanyIdResponse>(async () => {
            const bus = useEventBus<string>(eventBusName)

            const pagination = {
                page: 1,
                limit: 700,
            }

            const results = []
            let total = 0
            let fetchedAll = false

            while (!fetchedAll) {
                const { data } = await axios.get(`/api/metier/building`, {
                    params: {
                        _id_company,
                        page: pagination.page,
                        limit: pagination.limit,
                    },
                })

                if (pagination.page === 1) {
                    total = data.total
                }
                results.push(...data.data)

                bus.emit(
                    JSON.stringify({
                        total,
                        page: pagination.page,
                        limit: pagination.limit,
                    })
                )

                if (results.length >= total) {
                    fetchedAll = true
                } else {
                    pagination.page++
                }
            }
            bus.reset()

            return {
                data: results,
                total,
            }
        })
    }

    /**
     * Récupère les informations d'un bâtiment par son identifiant
     * @param _id_building - Identifiant du bâtiment
     * @param controller - Contrôleur d'annulation
     * @returns Les informations du bâtiment
     */
    async getBuildingById(
        _id_building: string,
        options: { children?: boolean } = { children: false },
        controller?: AbortController
    ): Promise<BuildingByIdResponse> {
        return handleServiceCall<BuildingByIdResponse>(async () => {
            const { data } = await axios.get(
                `/api/metier/building/${_id_building}`,
                {
                    params: {
                        children: options.children,
                    },
                    signal: controller?.signal,
                }
            )
            return data
        })
    }
    /**
     * Récupère les informations d'un batiment par son identifiant
     * @param _id_source - Identifiant de la source
     * @param date_from - Date de début au format YYYY-MM-DD
     * @param date_to - Date de fin au format YYYY-MM-DD
     * @param freq - Fréquence (`raw`, `hourly`, `daily`, `weekly`, `monthly`)
     * @param variables - Liste des variables à récupérer au format x_y_z.stat,
     * si non définis alors la liste est calculée selon la source.
     *
     * @returns Les données au format JSON string
     */
    async getDataBuildingById(
        _id_building: string,
        date_from: string,
        date_to: string,
        freq: string,
        variables: any[] | null
    ): Promise<DataBuildingByIdResponse> {
        return handleServiceCall<DataBuildingByIdResponse>(async () => {
            const { data } = await axios.get(
                `/api/dapi/building/${_id_building}/data`,
                {
                    params: {
                        date_from,
                        date_to,
                        freq,
                        variables,
                    },
                    paramsSerializer: paramsSerializer,
                }
            )
            return data
        })
    }
    /**
     * Récupère tous les workflows Enedis pour un bâtiment
     * @param _id_building - Identifiant du bâtiment
     */
    async getAllEnedisWorkflowByBuildingId(_id_building: string) {
        return handleServiceCall<WorkflowEnedisResponseBuilding>(async () => {
            const { data } = await axios.get(
                `/api/metier/v5/workflow/data/enedis/building/${_id_building}`,
                {
                    params: {
                        limit: 10,
                        // regex pour récupérer les workflows de type update_one et historique_*
                        nameRegex: "^(update_once|historique_.*)",
                        // seulement les workflows en cours d'exécution
                        state: "run",
                    },
                }
            )
            return data
        })
    }

    /**
     * Récupère l'arbre des dossiers d'un utilisateur
     * @param _id_folder - Identifiant du dossier
     */
    async getFolderTree(_id_folder?: string): Promise<any> {
        return handleServiceCall(async () => {
            const { data } = await axios.get(`/api/metier/v5/folder/tree`, {
                params: _id_folder ? { _id_folder } : {},
            })
            return data
        })
    }

    /**
     * Crée un tag pour un bâtiment
     * @param _id_building - Identifiant du bâtiment
     * @param tags - Tag(s) à ajouter
     */
    async createBuildingTag(
        _id_building: string,
        tags: string[]
    ): Promise<void> {
        return handleServiceCall(async () => {
            await axios.post(`/api/metier/building/${_id_building}/tag`, {
                tags,
            })
        })
    }

    /**
     * Crée un tag pour une entreprise
     * @param tags - Tag(s) à ajouter
     */
    async addCompanyTag(_id_company: string, tags: string[]): Promise<void> {
        return handleServiceCall(async () => {
            await axios.post(`/api/metier/company/building/tag`, {
                tags,
            })
        })
    }

    /**
     * Supprimer un tag pour une entreprise
     * @param tags - Tag(s) à ajouter
     */
    async deleteCompanyTag(tags: string[]): Promise<void> {
        return handleServiceCall(async () => {
            await axios.delete(`/api/metier/company/building/tag`, {
                data: {
                    tags,
                },
            })
        })
    }

    /**
     * Supprime un tag pour un bâtiment
     * @param _id_building - Identifiant du bâtiment
     * @param tags - Tag(s) à supprimer
     * @returns
     */
    async deleteBuildingTag(
        _id_building: string,
        tags: string[]
    ): Promise<void> {
        return handleServiceCall(async () => {
            await axios.delete(`/api/metier/building/${_id_building}/tag`, {
                data: {
                    tags,
                },
            })
        })
    }

    /**
     * Récupère les tags d'une organisation
     * @param _id_company - Identifiant de l'organisation
     * @returns Liste des tags
     */
    async getTags(): Promise<string[]> {
        return handleServiceCall(async () => {
            const {
                data: { data },
            } = await axios.get(`/api/metier/company/building/tag`)
            return data
        })
    }

    /**
     * Rattache une source à un bâtiment
     * @param payload - Données de rattachement de la source, incluant l'identifiant du bâtiment et de la source
     * @returns Le bâtiment mis à jour
     */
    async addBuildingSource(payload: BuildingSourcePayload): Promise<Building> {
        return handleServiceCall<Building>(async () => {
            const { buildingId, sourceId, ...requestPayload } = payload
            const { data } = await axios.post(
                `/api/metier/v5/building/${buildingId}/source/${sourceId}`,
                requestPayload
            )
            return data
        })
    }

    /**
     * Met à jour le rattachement d'une source à un bâtiment
     * @param payload - Données de mise à jour du rattachement, incluant les identifiants du bâtiment et de la source
     * @returns Le bâtiment mis à jour
     */
    async updateBuildingSource(
        payload: BuildingSourcePayload
    ): Promise<Building> {
        return handleServiceCall<Building>(async () => {
            const { buildingId, sourceId, ...requestPayload } = payload
            const { data } = await axios.put(
                `/api/metier/v5/building/${buildingId}/source/${sourceId}`,
                requestPayload
            )
            return data
        })
    }

    /**
     * Supprime le rattachement d'une source à un bâtiment
     * @param buildingId - Identifiant du bâtiment
     * @param sourceId - Identifiant de la source
     */
    async removeBuildingSource(
        buildingId: string,
        sourceId: string
    ): Promise<Building> {
        return handleServiceCall<Building>(async () => {
            const { data } = await axios.delete(
                `/api/metier/v5/building/${buildingId}/source/${sourceId}`
            )
            return data ?? null
        })
    }

    /**
     * Active une source d'un bâtiment (isUsed = true)
     * PUT /v5/building/:_id_building/source/:_id_source/use
     * @param buildingId - Identifiant du bâtiment
     * @param sourceId - Identifiant de la source
     * @returns Le bâtiment mis à jour
     */
    async updateBuildingSourceUse(
        buildingId: string,
        sourceId: string,
        controller?: AbortController
    ): Promise<Building> {
        return handleServiceCall<Building>(async () => {
            const { data } = await axios.put(
                `/api/metier/v5/building/${buildingId}/source/${sourceId}/use`,
                {
                    signal: controller?.signal,
                }
            )
            return data
        })
    }

    /**
     * Désactive une source d'un bâtiment (isUsed = false)
     * PUT /v5/building/:_id_building/source/:_id_source/unuse
     * @param buildingId - Identifiant du bâtiment
     * @param sourceId - Identifiant de la source
     * @returns Le bâtiment mis à jour
     */
    async updateBuildingSourceUnuse(
        buildingId: string,
        sourceId: string,
        controller?: AbortController
    ): Promise<Building> {
        return handleServiceCall<Building>(async () => {
            const { data } = await axios.put(
                `/api/metier/v5/building/${buildingId}/source/${sourceId}/unuse`,
                {
                    signal: controller?.signal,
                }
            )
            return data
        })
    }

    /**
     * Met à jour la station météo de référence d'un bâtiment
     * PUT /v5/building/:_id_building/meteo/:_id_source
     * @param buildingId - Identifiant du bâtiment
     * @param sourceId - Identifiant de la nouvelle station météo
     * @returns Le bâtiment mis à jour
     */
    async updateBuildingWeatherStation(
        buildingId: string,
        sourceId: string,
        controller?: AbortController
    ): Promise<Building> {
        return handleServiceCall<Building>(async () => {
            const { data } = await axios.put(
                `/api/metier/v5/building/${buildingId}/meteo/${sourceId}`,
                {
                    signal: controller?.signal,
                }
            )
            return data
        })
    }

    /**
     * Recherche des bâtiments par nom ou adresse
     * @param query - Terme de recherche
     * @returns Liste des bâtiments correspondant à la recherche
     */
    async searchBuildings(query: string): Promise<Building[]> {
        return handleServiceCall<Building[]>(async () => {
            const {
                data: { data },
            } = await axios.get(`/api/metier/building`, {
                params: {
                    search: query,
                },
            })
            return data
        })
    }

    /**
     * Crée un bâtiment pour l'entreprise de l'utilisateur connecté.
     * POST /api/metier/building (body: name, address, level, occupation, id_building, parcel_location?, _id_folder)
     * @param name - Nom du bâtiment
     * @param address - Adresse du bâtiment
     * @param options - Options (level, occupation, id_building, parcel_location, _id_folder)
     * @returns Le bâtiment créé
     */
    async createBuilding(
        name: string,
        address: Address,
        options: CreateBuildingOptions = {}
    ): Promise<Building> {
        return handleServiceCall<Building>(async () => {
            const {
                level,
                occupation,
                id_building,
                parcel_location,
                _id_folder,
            } = options
            const { data } = await axios.post(`/api/metier/building`, {
                name,
                address,
                level,
                occupation,
                id_building,
                _id_folder,
                ...(id_building && parcel_location ? { parcel_location } : {}),
            })
            return data
        })
    }

    /**
     * Crée plusieurs bâtiments en lot.
     * POST /api/metier/building/batch
     * @param items - Entrées (nom, adresse, localisation)
     * @returns Bâtiments créés (avec `_id`)
     */
    async createBuildings(
        items: CreateBuildingBatchItem[]
    ): Promise<Building[]> {
        return handleServiceCall<Building[]>(async () => {
            const { data } = await axios.post(`/api/metier/building/batch`, {
                data: items,
            })
            return data
        })
    }

    /**
     * Supprime un bâtiment.
     * DELETE /api/metier/building/:_id_building
     * @param _id_building - Identifiant Mongo du bâtiment
     */
    async deleteBuilding(_id_building: string): Promise<void> {
        return handleServiceCall(async () => {
            await axios.delete(`/api/metier/building/${_id_building}`)
        })
    }

    /**
     * Ajoute des bâtiments enfants à un bâtiment parent.
     * POST /api/metier/v5/building/:_id_building/children (body: { buildings: string[] })
     * @param buildingId - Identifiant du bâtiment parent
     * @param childrenIds - Identifiants des bâtiments enfants à lier
     * @returns Le bâtiment parent mis à jour
     */
    async addBuildingChildren(
        buildingId: string,
        childrenIds: string[]
    ): Promise<Building> {
        return handleServiceCall<Building>(async () => {
            const { data } = await axios.post(
                `/api/metier/v5/building/${buildingId}/children`,
                { buildings: childrenIds }
            )
            return data
        })
    }

    /**
     * Met à jour l'adresse (et niveau, id_building) d'un bâtiment.
     * PUT /api/metier/building/:_id_building/address
     * @param buildingId - Identifiant du bâtiment
     * @param payload - address, level?, id_building?
     * @returns Le bâtiment mis à jour
     */
    async updateBuildingAddress(
        buildingId: string,
        payload: UpdateBuildingAddressPayload
    ): Promise<Building> {
        return handleServiceCall<Building>(async () => {
            const { data } = await axios.put(
                `/api/metier/building/${buildingId}/address`,
                payload
            )
            return data
        })
    }

    /**
     * Bâtiments à proximité (liens parent / enfant).
     * GET /api/metier/v5/building/:_id_building/near
     */
    async getBuildingsNear(
        buildingId: string,
        radius: number
    ): Promise<Building[]> {
        return handleServiceCall<Building[]>(async () => {
            const { data } = await axios.get<Building[]>(
                `/api/metier/v5/building/${buildingId}/near`,
                {
                    params: {
                        restrictByLevel: true,
                        maxDist: radius,
                    },
                }
            )
            return data ?? []
        })
    }

    /**
     * Valide la parcelle cadastrale (BNPB).
     * PUT /api/metier/building/:_id_building/validation
     * @param parcel_location - Optionnel : géométrie parcelle (MultiPolygon) si l’open data ne fournit pas les coordonnées.
     */
    async validateBuildingParcel(
        _id_building: string,
        id_building: string,
        parcel_location?: ParcelLocationMultiPolygon
    ): Promise<Building> {
        return handleServiceCall<Building>(async () => {
            const body: {
                id_building: string
                parcel_location?: ParcelLocationMultiPolygon
            } = { id_building }
            if (parcel_location) {
                body.parcel_location = parcel_location
            }
            const { data } = await axios.put(
                `/api/metier/building/${_id_building}/validation`,
                body
            )
            return data
        })
    }

    /**
     * Retire des bâtiments enfants d’un parent.
     * DELETE /api/metier/v5/building/:_id_building/children
     */
    async removeBuildingChildren(
        buildingId: string,
        childrenIds: string[]
    ): Promise<Building> {
        return handleServiceCall(async () => {
            const { data } = await axios.delete(
                `/api/metier/v5/building/${buildingId}/children`,
                {
                    params: { buildings: childrenIds },
                    paramsSerializer,
                }
            )
            return data
        })
    }
}
